import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../services/api";
import StaffNavbar from "../components/StaffNavbar";
import BackButton from "../components/BackButton";
import "../styles/StaffPages.css";

const FALLBACK_COLOR = "#0F2439";

function hexToRgb(hex) {
    const h = hex.replace("#", "");
    return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
    };
}

function makeBgColor(hex) {
    if (!hex) return "#f0f0f0";
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, 0.1)`;
}
const PHASES = [
    { key: "planning", label: "Planning" },
    { key: "setup", label: "Setup" },
    { key: "integration", label: "Integration" },
    { key: "launch-prep", label: "Launch prep" },
    { key: "live", label: "Live" },
];
const SCHOOL_DATA = {
    "utah-state": {
        sections: [
            {
                label: "Apr 6–10",
                range: "April 6 – 10",
                phase: 0,
                tasks: [
                    "Locking in committed restaurants",
                    "Setting up more restaurant meetings for next week in Logan",
                    "Prepping for whiteboarding meeting — integration, perks/rewards list and cadence, and re-launch marketing timeline",
                ],
                actions: [
                    "Get us a time for whiteboarding next week",
                    "Set up restaurant meetings for next week",
                    "Intro to Snap Mobile for integration talks to begin",
                ],
            },
            {
                label: "Apr 13–17",
                range: "April 13 – 17",
                phase: 1,
                tasks: [
                    "Meeting in Logan with your team — rewards (final point system + product roadmap to September), integration, and marketing for re-launch timeline",
                    "Meeting in Logan with restaurants to get them across the finish line",
                    "Onboard restaurants",
                ],
                actions: [
                    "Whiteboarding meeting to go through: rewards, integration, and marketing for re-launch timeline",
                ],
            },
            {
                label: "Apr 20–24",
                range: "April 20 – 24",
                phase: 2,
                tasks: [
                    "Work on integration",
                    "Onboard restaurants",
                    "Finalize perks list and cadence",
                ],
                actions: [
                    "Final check-off of experiences / perks",
                    "Start on execution of marketing plan",
                ],
            },
            {
                label: "Apr 27–May 1",
                range: "April 27 – May 1",
                phase: 2,
                tasks: [
                    "Check in on marketing plan",
                    "Continue integration conversations",
                    "Onboard restaurants",
                    "Upload perks / experiences for the year",
                ],
                actions: ["Marketing updates"],
            },
            {
                label: "May 4–8",
                range: "May 4 – 8",
                phase: 3,
                tasks: [
                    "Finalize marketing plan",
                    "Continue integration conversations",
                    "Onboard restaurants",
                    "Final launch prep",
                ],
                actions: ["Final launch prep"],
            },
            {
                label: "May 11–18",
                range: "May 11 – 18",
                phase: 4,
                tasks: ["Re-launch!"],
                actions: ["Re-launch!"],
            },
        ],
        updates: {
            0: [
                {
                    date: "Apr 6",
                    text: "Kicking off this week — locking in restaurants and prepping for our whiteboarding session. Please get us a time scheduled and intro us to Snap Mobile.",
                },
            ],
            1: [
                {
                    date: "Apr 13",
                    text: "Big week — we're in Logan for meetings with your team and restaurants. Let's nail down rewards, integration, and the re-launch marketing timeline.",
                },
            ],
            2: [
                {
                    date: "Apr 20",
                    text: "Integration work is underway. We need your final sign-off on perks and experiences this week, and marketing execution should kick off.",
                },
            ],
            3: [
                {
                    date: "Apr 27",
                    text: "Restaurants still onboarding. Perks and experiences getting uploaded. Send us your marketing updates.",
                },
            ],
            4: [
                {
                    date: "May 4",
                    text: "Final stretch — wrapping up integration, onboarding last restaurants, and prepping everything for launch.",
                },
            ],
            5: [{ date: "May 11", text: "We're live! Re-launch day." }],
        },
    },
    "boise-state": {
        sections: [
            {
                label: "Apr 6–10",
                range: "April 6 – 10",
                phase: 0,
                tasks: [
                    "Locking in committed restaurants",
                    "Setting up more restaurant meetings for next week in Boise",
                    "Prepping for whiteboarding meeting — integration, perks/rewards list and cadence, Learfield strategy, and launch test group",
                ],
                actions: [
                    "Get us a time for whiteboarding next week",
                    "Set up restaurant meetings for next week",
                    "Follow up with Snap Mobile for meeting this week",
                ],
            },
            {
                label: "Apr 13–17",
                range: "April 13 – 17",
                phase: 1,
                tasks: [
                    "Meeting in Boise with your team — rewards (final point system + product roadmap to September), integration, testing group for launch timeline, and Learfield strategy",
                    "Meeting in Boise with restaurants to get them across the finish line",
                    "Onboard restaurants",
                ],
                actions: [
                    "Whiteboarding meeting to go through: rewards, integration, and launch test group timeline",
                ],
            },
            {
                label: "Apr 20–24",
                range: "April 20 – 24",
                phase: 2,
                tasks: [
                    "Work on integration",
                    "Onboard restaurants",
                    "Finalize perks list and cadence",
                ],
                actions: [
                    "Final check-off of experiences / perks",
                    "Finalize testing group strategy",
                    "Finalize Learfield involvement",
                ],
            },
            {
                label: "Apr 27–May 1",
                range: "April 27 – May 1",
                phase: 2,
                tasks: [
                    "Check in on testing group plan",
                    "Continue integration conversations",
                    "Onboard restaurants",
                    "Upload perks / experiences for the year",
                ],
                actions: ["Set up meeting with testing group"],
            },
            {
                label: "May 4–8",
                range: "May 4 – 8",
                phase: 3,
                tasks: [
                    "Finalize testing plan",
                    "Continue integration conversations",
                    "Onboard restaurants",
                    "Final launch prep",
                ],
                actions: ["Final launch prep"],
            },
            {
                label: "May 11–18",
                range: "May 11 – 18",
                phase: 4,
                tasks: ["Launch!"],
                actions: ["Launch!"],
            },
        ],
        updates: {
            0: [
                {
                    date: "Apr 6",
                    text: "Kicking off this week — locking in restaurants and prepping for whiteboarding. Please get us a time scheduled and follow up with Snap Mobile.",
                },
            ],
            1: [
                {
                    date: "Apr 13",
                    text: "We're in Boise this week for meetings with your team and restaurants. Key topics: rewards, integration, Learfield strategy, and test group timeline.",
                },
            ],
            2: [
                {
                    date: "Apr 20",
                    text: "Integration underway. We need your final sign-off on perks, testing group strategy, and Learfield involvement this week.",
                },
            ],
            3: [
                {
                    date: "Apr 27",
                    text: "Restaurants onboarding continues. Perks getting uploaded. Please set up the meeting with your testing group.",
                },
            ],
            4: [
                {
                    date: "May 4",
                    text: "Final stretch — wrapping up testing plan, integration, and last restaurant onboarding. Launch prep time.",
                },
            ],
            5: [{ date: "May 11", text: "Launch day! Let's go." }],
        },
    },
    "west-virginia": {
        sections: [
            {
                label: "Apr 6–10",
                range: "April 6 – 10",
                phase: 0,
                tasks: [
                    "Setting up embedding convos with Sidearm",
                    "Perks and experience catalog",
                ],
                actions: [
                    "Intro to 3–5 restaurants this week",
                    "Intro to Sidearm (Erin)",
                ],
            },
            {
                label: "Apr 13–17",
                range: "April 13 – 17",
                phase: 1,
                tasks: [
                    "Restaurant meetings",
                    "Sidearm meetings",
                    "Present perks and experiences for the year",
                ],
                actions: [
                    "Lock in plans for May 13 — initial test group meeting",
                    "Feedback on perks and experiences",
                    "Stay on restaurants",
                ],
            },
            {
                label: "Apr 20–24",
                range: "April 20 – 24",
                phase: 1,
                tasks: [
                    "Restaurant meetings",
                    "Finalize perks and experiences (for initial test and expanded year)",
                ],
                actions: ["Finalize perks and experiences"],
            },
            {
                label: "Apr 27–May 1",
                range: "April 27 – May 1",
                phase: 2,
                tasks: [
                    "Testing plans — timeline, KPIs",
                    "Perks and experiences / cadence for initial test",
                    "Restaurant meetings",
                ],
                actions: ["Check off on presentation for test group"],
            },
            {
                label: "May 4–8",
                range: "May 4 – 8",
                phase: 3,
                tasks: ["Final prep"],
                actions: ["Final prep", "Check off on testing plan and timeline"],
            },
            {
                label: "May 11–18",
                range: "May 11 – 18",
                phase: 4,
                tasks: ["Launch! May 16: present to fan group"],
                actions: ["Launch! May 16: present to initial fan group"],
            },
        ],
        updates: {
            0: [
                {
                    date: "Apr 6",
                    text: "Getting started — setting up Sidearm embedding conversations and building out the perks catalog. Please intro us to 3–5 restaurants and connect us with Erin for Sidearm.",
                },
            ],
            1: [
                {
                    date: "Apr 13",
                    text: "Restaurant and Sidearm meetings this week. We'll present perks and experiences — please lock in plans for May 13 test group meeting and send feedback on perks.",
                },
            ],
            2: [
                {
                    date: "Apr 20",
                    text: "Continuing restaurant meetings. We need your final sign-off on perks and experiences this week.",
                },
            ],
            3: [
                {
                    date: "Apr 27",
                    text: "Testing plans coming together — timeline, KPIs, and initial test cadence. Please review and check off the presentation for the test group.",
                },
            ],
            4: [
                {
                    date: "May 4",
                    text: "Final prep week. Please check off on the testing plan and timeline.",
                },
            ],
            5: [
                {
                    date: "May 16",
                    text: "Launch day — presenting to the initial fan group. Let's make it happen.",
                },
            ],
        },
    },
    cincinnati: {
        sections: [
            {
                label: "Apr 6–10",
                range: "April 6 – 10",
                phase: 0,
                tasks: [
                    "Continue conversation with WMT",
                    "Perks and experience catalog",
                ],
                actions: ["Intro to 3–5 restaurants this week"],
            },
            {
                label: "Apr 13–17",
                range: "April 13 – 17",
                phase: 1,
                tasks: [
                    "Restaurant meetings",
                    "WMT meeting",
                    "Present perks and experiences for the year",
                ],
                actions: [
                    "Lock in plans for May 11 — initial test group meeting",
                    "Feedback on perks and experiences",
                    "Stay on restaurants",
                ],
            },
            {
                label: "Apr 20–24",
                range: "April 20 – 24",
                phase: 1,
                tasks: [
                    "Restaurant meetings",
                    "Finalize perks and experiences (for initial test and expanded year)",
                ],
                actions: ["Finalize perks and experiences"],
            },
            {
                label: "Apr 27–May 1",
                range: "April 27 – May 1",
                phase: 2,
                tasks: [
                    "Testing plan with Fan Council — timeline, KPIs",
                    "Perks and experiences / cadence for initial test",
                    "Restaurant meetings",
                ],
                actions: ["Check off on plan for test group"],
            },
            {
                label: "May 4–8",
                range: "May 4 – 8",
                phase: 3,
                tasks: ["Final prep on presentation"],
                actions: [
                    "Final prep",
                    "Check off on testing group presentation — timeline, KPIs",
                ],
            },
            {
                label: "May 11–18",
                range: "May 11 – 18",
                phase: 4,
                tasks: ["Launch! May 11: present to Fan Council"],
                actions: ["Launch! May 11: present to Fan Council"],
            },
        ],
        updates: {
            0: [
                {
                    date: "Apr 6",
                    text: "Getting started — continuing WMT conversations and building the perks catalog. Please intro us to 3–5 restaurants this week.",
                },
            ],
            1: [
                {
                    date: "Apr 13",
                    text: "Restaurant and WMT meetings this week. We'll present perks and experiences — please lock in plans for the May 11 Fan Council presentation and send feedback on perks.",
                },
            ],
            2: [
                {
                    date: "Apr 20",
                    text: "Continuing restaurant meetings. We need your final sign-off on perks and experiences this week.",
                },
            ],
            3: [
                {
                    date: "Apr 27",
                    text: "Testing plan with Fan Council taking shape — timeline, KPIs, and initial test cadence. Please review and check off the plan for the test group.",
                },
            ],
            4: [
                {
                    date: "May 4",
                    text: "Final prep week. Please check off on the testing group presentation, timeline, and KPIs.",
                },
            ],
            5: [
                {
                    date: "May 11",
                    text: "Launch day — presenting to Fan Council. Let's go.",
                },
            ],
        },
    },
    creighton: {
        sections: [
            {
                label: "Apr 6–10",
                range: "April 6 – 10",
                phase: 0,
                tasks: [
                    "Present outline of presentation",
                    "Timeline planning — Omaha visit, embedding, testing / pushing to the masses",
                    "Landing page / restaurants",
                ],
                actions: ["Intro to 3–5 restaurants this week", "Intro to Sidearm"],
            },
            {
                label: "Apr 13–17",
                range: "April 13 – 17",
                phase: 1,
                tasks: [
                    "Restaurant meetings",
                    "Sidearm conversations",
                    "Present perks and experiences for the year",
                ],
                actions: [
                    "Lock in dates for May 13 — athletic department meeting and initial fan group meeting",
                    "Feedback on perks and experiences",
                ],
            },
            {
                label: "Apr 20–24",
                range: "April 20 – 24",
                phase: 2,
                tasks: [
                    "Finalize perks and experiences (for initial test and expanded year)",
                ],
                actions: ["Finalize perks and experiences", "Finalize May 13 plans"],
            },
            {
                label: "Apr 27–May 1",
                range: "April 27 – May 1",
                phase: 2,
                tasks: ["Update on presentation", "Testing plans — KPIs, timeline"],
                actions: ["Check off on presentation"],
            },
            {
                label: "May 4–8",
                range: "May 4 – 8",
                phase: 3,
                tasks: ["Final prep", "Testing plans — KPIs, timeline"],
                actions: ["Final prep", "Check off on testing plan and timeline"],
            },
            {
                label: "May 11–18",
                range: "May 11 – 18",
                phase: 4,
                tasks: ["Launch! May 13: present to athletic department and fan group"],
                actions: [
                    "Launch! May 13: present to athletic department and fan group",
                ],
            },
        ],
        updates: {
            0: [
                {
                    date: "Apr 6",
                    text: "Getting started — we're outlining the presentation and building the timeline for the Omaha visit, embedding, and testing phases. Please intro us to 3–5 restaurants and Sidearm this week.",
                },
            ],
            1: [
                {
                    date: "Apr 13",
                    text: "Meeting with restaurants and Sidearm this week. We'll present perks and experiences — please lock in dates for the May 13 athletic department and fan group meetings.",
                },
            ],
            2: [
                {
                    date: "Apr 20",
                    text: "Finalizing perks and experiences for the initial test and expanded year. Please confirm your final selections and lock down May 13 plans.",
                },
            ],
            3: [
                {
                    date: "Apr 27",
                    text: "Presentation update coming your way. Testing plans taking shape with KPIs and timeline. We need your sign-off on the presentation.",
                },
            ],
            4: [
                {
                    date: "May 4",
                    text: "Final prep week. Please review and check off on the testing plan and timeline.",
                },
            ],
            5: [
                {
                    date: "May 13",
                    text: "Launch day — presenting to athletic department and fan group. Let's make it count.",
                },
            ],
        },
    },
};
function useChecked(schoolId) {
    const [checked, setChecked] = useState({});
    useEffect(() => {
        try {
            const raw = localStorage.getItem(`checks:${schoolId}`);
            if (raw) setChecked(JSON.parse(raw));
        } catch { }
    }, [schoolId]);
    const toggle = useCallback(
        (weekIdx, actionIdx) => {
            setChecked((prev) => {
                const key = `${weekIdx}-${actionIdx}`;
                const next = { ...prev, [key]: !prev[key] };
                try {
                    localStorage.setItem(`checks:${schoolId}`, JSON.stringify(next));
                } catch { }
                return next;
            });
        },
        [schoolId],
    );
    return [checked, toggle];
}
function SchoolBadge({ school, size = 44 }) {
    if (school.image) {
        return (
            <img
                src={school.image}
                alt={school.name}
                style={{
                    width: size,
                    height: size,
                    minWidth: size,
                    borderRadius: "12px",
                    objectFit: "contain",
                }}
            />
        );
    }
    const abbr = school.abbr || school.name?.substring(0, 2).toUpperCase() || "?";
    const color = school.primary_color || FALLBACK_COLOR;
    const fontSize =
        abbr.length >= 3 ? Math.round(size * 0.3) : Math.round(size * 0.38);
    return (
        <div
            style={{
                width: size,
                height: size,
                minWidth: size,
                borderRadius: "12px",
                background: color,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: `${fontSize}px`,
                fontFamily: "'Baikal Book', sans-serif",
                letterSpacing: abbr.length >= 3 ? "-0.5px" : "0.5px",
                lineHeight: 1,
            }}
        >
            {abbr}
        </div>
    );
}
function SchoolPicker() {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(null);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const response = await apiService.getSchools();
                const schoolList = Array.isArray(response) ? response : (response.results || []);
                setSchools(schoolList.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
            } catch (err) {
                console.error("Failed to fetch schools:", err);
                setError("Failed to load schools. Make sure you're logged in as a superuser.");
            } finally {
                setLoading(false);
            }
        };
        fetchSchools();
    }, []);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontFamily: "'Baikal Book', sans-serif",
                padding: "2rem 0",
            }}
        >


            <div style={{ marginBottom: "3rem", textAlign: "center" }}>

                <h1
                    style={{
                        fontFamily: "'Baikal Condensed', sans-serif",
                        fontSize: "clamp(28px, 5vw, 42px)",
                        fontWeight: 400,
                        margin: "0 0 0.5rem",
                        color: "#1a1a1a",
                        letterSpacing: "-0.5px",
                    }}
                >
                    Onboarding hub
                </h1>

                <p style={{ fontSize: "16px", color: "#6b6b6b", margin: 0 }}>
                    Select your school to view your dashboard
                </p>

            </div>

            {loading ? (
                <p style={{ color: "#999", fontSize: "15px" }}>Loading schools...</p>
            ) : (() => {
                const withLogo = schools.filter(s => s.image);
                const withoutLogo = schools.filter(s => !s.image);
                return (
                    <div style={{ width: "100%", maxWidth: "720px" }}>
                        {withLogo.length > 0 && (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                    gap: "12px",
                                    marginBottom: withoutLogo.length > 0 ? "2rem" : 0,
                                }}
                            >
                                {withLogo.map((s, i) => (
                                    <button
                                        key={s.id}
                                        onClick={() => navigate(`/onboarding/${s.id}/`)}
                                        onMouseEnter={() => setHovered(s.id)}
                                        onMouseLeave={() => setHovered(null)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "14px",
                                            padding: "18px 20px",
                                            border: "1px solid",
                                            borderColor: hovered === s.id ? "#1a1a1a" : "#e2e2e2",
                                            borderRadius: "14px",
                                            background: hovered === s.id ? "#fafafa" : "#fff",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            transform: hovered === s.id ? "translateY(-2px)" : "none",
                                            boxShadow: hovered === s.id ? "0 4px 20px rgba(0,0,0,0.06)" : "none",
                                            fontFamily: "'Baikal Book', sans-serif",
                                        }}
                                    >
                                        <SchoolBadge school={s} />
                                        <span style={{ fontSize: "15px", fontWeight: 500, color: "#1a1a1a", textAlign: "left" }}>
                                            {s.name}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {withoutLogo.length > 0 && (
                            <>
                                {withLogo.length > 0 && (
                                    <div style={{ fontSize: "12px", color: "#999", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "10px" }}>
                                        Other schools
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                        gap: "8px",
                                    }}
                                >
                                    {withoutLogo.map((s) => (
                                        <button
                                            key={s.id}
                                            onClick={() => navigate(`/onboarding/${s.id}/`)}
                                            onMouseEnter={() => setHovered(s.id)}
                                            onMouseLeave={() => setHovered(null)}
                                            style={{
                                                padding: "12px 16px",
                                                border: "1px solid",
                                                borderColor: hovered === s.id ? "#1a1a1a" : "#e2e2e2",
                                                borderRadius: "10px",
                                                background: hovered === s.id ? "#fafafa" : "#fff",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",
                                                fontFamily: "'Baikal Book', sans-serif",
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                color: "#1a1a1a",
                                                textAlign: "left",
                                            }}
                                        >
                                            {s.name}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                );
            })()}

        </div>
    );
}
function Dashboard({ school, onBack }) {
    const [weekIdx, setWeekIdx] = useState(0);
    const data = SCHOOL_DATA[school.id] || SCHOOL_DATA[school.name?.toLowerCase().replace(/\s+/g, "-")];
    const [checked, toggle] = useChecked(school.id);
    const accent = school.primary_color || FALLBACK_COLOR;
    const accentBg = makeBgColor(accent);

    if (!data) {
        return (
            <div style={{ fontFamily: "'Baikal Book', sans-serif", maxWidth: 680, margin: "0 auto", padding: "3rem 1rem", textAlign: "center" }}>
                <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap" rel="stylesheet" />
                <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", color: "#999", fontSize: "14px", display: "inline-flex", alignItems: "center", gap: "4px", fontFamily: "'Baikal Book', sans-serif", marginBottom: "2rem" }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    All schools
                </button>
                <SchoolBadge school={school} size={64} />
                <h2 style={{ fontFamily: "'Baikal Condensed', sans-serif", marginTop: "1rem", color: "#1a1a1a" }}>{school.name}</h2>
                <p style={{ color: "#999", fontSize: "15px", marginTop: "0.5rem" }}>No onboarding timeline configured for this school yet.</p>
            </div>
        );
    }

    const week = data.sections[weekIdx];
    const totalActions = data.sections.reduce((sum, w) => sum + w.actions.length, 0);
    const completedActions = Object.values(checked).filter(Boolean).length;
    const weekDone = week.actions.filter(
        (_, ai) => checked[`${weekIdx}-${ai}`],
    ).length;
    const weekComplete = weekDone === week.actions.length;
    const updates = data.updates[weekIdx] || [];
    return (
        <div
            style={{
                fontFamily: "'Baikal Book', sans-serif",
                maxWidth: 680,
                margin: "0 auto",
                padding: "1.5rem 1rem 3rem",
            }}
        >


            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "2rem",
                }}
            >

                {onBack && (
                    <button
                        onClick={onBack}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "6px",
                            color: "#999",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontFamily: "'Baikal Book', sans-serif",
                        }}
                    >

                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M10 12L6 8l4-4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        All schools
                    </button>
                )}

                <div style={{ flex: 1 }} />

                <SchoolBadge school={school} size={32} />

            </div>

            <h1
                style={{
                    fontFamily: "'Baikal Condensed', sans-serif",
                    fontSize: "clamp(24px, 4vw, 34px)",
                    fontWeight: 400,
                    margin: "0 0 0.25rem",
                    color: "#1a1a1a",
                    letterSpacing: "-0.3px",
                }}
            >
                {school.name}
            </h1>

            <p style={{ fontSize: "15px", color: "#888", margin: "0 0 2rem" }}>
                Onboarding progress
            </p>

            <div style={{ marginBottom: "2rem" }}>

                <div style={{ display: "flex", gap: "3px", marginBottom: "8px" }}>

                    {PHASES.map((p, i) => (
                        <div
                            key={p.key}
                            style={{
                                flex: 1,
                                height: "5px",
                                borderRadius:
                                    i === 0 ? "3px 0 0 3px" : i === 4 ? "0 3px 3px 0" : "0",
                                background: i <= week.phase ? accent : "#eee",
                                transition: "background 0.3s ease",
                            }}
                        />
                    ))}

                </div>

                <div style={{ display: "flex", justifyContent: "space-between" }}>

                    {PHASES.map((p, i) => (
                        <span
                            key={p.key}
                            style={{
                                fontSize: "11px",
                                fontWeight: i === week.phase ? 600 : 400,
                                color: i === week.phase ? accent : "#bbb",
                                transition: "color 0.3s ease",
                            }}
                        >
                            {p.label}
                        </span>
                    ))}

                </div>

            </div>

            <div
                style={{
                    display: "flex",
                    gap: "6px",
                    overflowX: "auto",
                    marginBottom: "2rem",
                    paddingBottom: "4px",
                }}
            >

                {data.sections.map((w, i) => (
                    <button
                        key={i}
                        onClick={() => setWeekIdx(i)}
                        style={{
                            minWidth: "88px",
                            padding: "8px 14px",
                            border: "1px solid",
                            borderColor: i === weekIdx ? accent : "#e8e8e8",
                            borderRadius: "10px",
                            background: i === weekIdx ? accentBg : "transparent",
                            color: i === weekIdx ? accent : "#888",
                            fontSize: "13px",
                            fontWeight: i === weekIdx ? 600 : 400,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            whiteSpace: "nowrap",
                            fontFamily: "'Baikal Book', sans-serif",
                        }}
                    >
                        {w.label}
                    </button>
                ))}

            </div>

            <div style={{ display: "flex", gap: "12px", marginBottom: "2rem" }}>

                <div
                    style={{
                        flex: 1,
                        padding: "16px 18px",
                        background: "#f8f8f6",
                        borderRadius: "12px",
                    }}
                >

                    <div
                        style={{
                            fontSize: "12px",
                            color: "#999",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        This week
                    </div>

                    <div
                        style={{
                            fontSize: "22px",
                            fontWeight: 600,
                            color: weekComplete ? "#2D7A50" : "#1a1a1a",
                        }}
                    >
                        {weekComplete ? "All done" : `${weekDone} / ${week.actions.length}`}
                    </div>

                </div>

                <div
                    style={{
                        flex: 1,
                        padding: "16px 18px",
                        background: "#f8f8f6",
                        borderRadius: "12px",
                    }}
                >

                    <div
                        style={{
                            fontSize: "12px",
                            color: "#999",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        Overall
                    </div>

                    <div style={{ fontSize: "22px", fontWeight: 600, color: "#1a1a1a" }}>
                        {totalActions > 0
                            ? Math.round((completedActions / totalActions) * 100)
                            : 0}
                        %
                    </div>

                </div>

            </div>

            <section style={{ marginBottom: "2rem" }}>

                <h2
                    style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#999",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                        margin: "0 0 12px",
                    }}
                >
                    What we're working on
                </h2>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>

                    {week.tasks.map((t, i) => (
                        <div
                            key={i}
                            style={{
                                padding: "12px 16px",
                                borderRadius: "10px",
                                border: "1px solid #eee",
                                fontSize: "14px",
                                color: "#444",
                                lineHeight: 1.5,
                            }}
                        >
                            {t}
                        </div>
                    ))}

                </div>

            </section>

            <section style={{ marginBottom: "2rem" }}>

                <h2
                    style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: accent,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                        margin: "0 0 12px",
                    }}
                >
                    Your action items
                </h2>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>

                    {week.actions.map((a, ai) => {
                        const done = checked[`${weekIdx}-${ai}`];
                        return (
                            <button
                                key={ai}
                                onClick={() => toggle(weekIdx, ai)}
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "12px",
                                    padding: "12px 16px",
                                    borderRadius: "10px",
                                    border: "1px solid",
                                    borderColor: done ? "#e0e0e0" : accent + "33",
                                    background: done ? "#fafafa" : accentBg + "88",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    transition: "all 0.2s ease",
                                    fontFamily: "'Baikal Book', sans-serif",
                                    width: "100%",
                                }}
                            >

                                <div
                                    style={{
                                        width: 20,
                                        height: 20,
                                        minWidth: 20,
                                        borderRadius: "6px",
                                        border: done ? "none" : `1.5px solid ${accent}`,
                                        background: done ? accent : "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginTop: "1px",
                                        transition: "all 0.2s ease",
                                    }}
                                >

                                    {done && (
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path
                                                d="M2.5 6L5 8.5L9.5 3.5"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}

                                </div>

                                <span
                                    style={{
                                        fontSize: "14px",
                                        color: done ? "#bbb" : "#333",
                                        textDecoration: done ? "line-through" : "none",
                                        lineHeight: 1.5,
                                        transition: "color 0.2s ease",
                                    }}
                                >
                                    {a}
                                </span>

                            </button>
                        );
                    })}

                </div>

            </section>

            <section>

                <h2
                    style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#999",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                        margin: "0 0 12px",
                    }}
                >
                    Updates
                </h2>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

                    {updates.map((u, i) => (
                        <div
                            key={i}
                            style={{
                                paddingLeft: "14px",
                                borderLeft: "2px solid #e0e0e0",
                                borderRadius: 0,
                            }}
                        >

                            <div
                                style={{
                                    fontSize: "11px",
                                    color: "#bbb",
                                    marginBottom: "3px",
                                    fontWeight: 500,
                                }}
                            >
                                {u.date}
                            </div>

                            <div style={{ fontSize: "14px", color: "#555", lineHeight: 1.6 }}>
                                {u.text}
                            </div>

                        </div>
                    ))}

                </div>

            </section>

            <div
                style={{
                    marginTop: "3rem",
                    paddingTop: "1.5rem",
                    borderTop: "1px solid #eee",
                    textAlign: "center",
                    fontSize: "13px",
                    color: "#ccc",
                }}
            >
                Questions? Reach out to your onboarding contact anytime.
            </div>

        </div>
    );
}
export default function TimelinePage() {
    return (
        <div className="admin-dashboard">
            <StaffNavbar activeSection="superuser" />
            <div className="admin-main">
                <div className="admin-content">
                    <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
                    <SchoolPicker />
                </div>
            </div>
        </div>
    );
}
